import { useDl } from "../utils/dlToken";
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Gavel, Search, Paperclip, Mail, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ST = { awaiting: "Čaká na vyjadrenie", responded: "Vyjadril sa – na rozhodnutie", warned: "Napomenutý", revoked: "Certifikát odobratý", closed: "Zastavené", reinstated: "Certifikát vrátený" };
const KIND = { professional: "profesionál", company: "firma", recruiter: "preverený / absolvent" };
const fmt = (d) => (d ? new Date(d).toLocaleDateString("sk-SK") : "");
const inp = "w-full bg-black border border-white/15 px-3 py-2 text-sm focus:outline-none focus:border-[#D4AF37]";

function StartCase({ s, hdr, onDone }) {
  const [reason, setReason] = useState("");
  const [msg, setMsg] = useState("");
  const start = async () => {
    if (!window.confirm(`Začať konanie voči ${s.name}? ${s.email ? "Odošle sa e-mail s výzvou a lehotou 30 dní." : "Bez e-mailu – dotyčného kontaktujte sami."}`)) return;
    try { await axios.post(`${API}/admin/revocations`, { certificate_no: s.certificate_no, name: s.name, kind: s.kind, reason }, hdr); onDone(); }
    catch (e) { setMsg(e?.response?.data?.detail || "Chyba"); }
  };
  return (
    <div className="mt-3 space-y-2">
      <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Dôvod konania – čo porušil (dotyčný ho uvidí vo výzve)" className={inp} data-testid={`rev-reason-${s.certificate_no}`} />
      <button onClick={start} disabled={reason.trim().length < 10} className="bg-red-600 text-white text-xs px-4 py-2 disabled:opacity-40" data-testid={`rev-start-${s.certificate_no}`}>Odoslať výzvu na vyjadrenie</button>
      {msg && <p className="text-red-300 text-xs">{msg}</p>}
    </div>
  );
}

function Hit({ s, hdr, onDone }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/10 p-3 text-sm" data-testid={`rev-hit-${s.certificate_no}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p>{s.name} <span className="text-[#D4AF37] font-mono text-xs">{s.certificate_no}</span> <span className="text-white/40 text-xs">· {KIND[s.kind]}</span></p>
        <div className="flex items-center gap-2 text-xs">
          {s.blacklist_hits > 0 && <span className="text-red-300 inline-flex items-center gap-1"><AlertTriangle size={12} /> hlásenia: {s.blacklist_hits}</span>}
          <span className="text-white/40 inline-flex items-center gap-1"><Mail size={12} /> {s.email || "bez e-mailu"}{!s.has_account && " · bez účtu"}</span>
          {s.open_case ? <span className="text-amber-300">konanie prebieha</span>
            : <button onClick={() => setOpen((v) => !v)} className="border border-red-400/60 text-red-300 px-3 py-1" data-testid={`rev-open-${s.certificate_no}`}>Začať konanie o odobratí</button>}
        </div>
      </div>
      {open && <StartCase s={s} hdr={hdr} onDone={() => { setOpen(false); onDone(); }} />}
    </div>
  );
}

function Case({ r, hdr, token, onDone }) {
  const dl = useDl(token);
  const [note, setNote] = useState("");
  const act = async (action, ask) => {
    if (!window.confirm(ask)) return;
    try { await axios.post(`${API}/admin/revocations/${r.id}/decide`, { action, note }, hdr); onDone(); } catch (e) { alert(e?.response?.data?.detail || "Chyba"); }
  };
  const open = ["awaiting", "responded"].includes(r.status);
  const b = "px-3 py-1.5 text-xs border";
  return (
    <div className="border border-white/10 p-4 text-sm" data-testid={`rev-case-${r.id}`}>
      <div className="flex flex-wrap justify-between gap-2">
        <p>{r.name} <span className="text-[#D4AF37] font-mono text-xs">{r.certificate_no}</span></p>
        <span className="text-xs text-white/50" data-testid={`rev-status-${r.id}`}>{ST[r.status]} · začaté {fmt(r.created_at)}{open && ` · lehota do ${fmt(r.deadline)}`}{r.status === "awaiting" && new Date(r.deadline) < new Date() && <span className="text-amber-300" data-testid={`rev-expired-${r.id}`}> · lehota uplynula – môžete rozhodnúť</span>}</span>
      </div>
      <p className="text-white/70 mt-2 whitespace-pre-wrap"><span className="text-white/40">Dôvod:</span> {r.reason}</p>
      {r.response ? (
        <div className="mt-2 border-l-2 border-[#D4AF37] pl-3" data-testid={`rev-response-${r.id}`}>
          <p className="text-[#D4AF37] text-xs">Vyjadrenie · {fmt(r.response.at)}</p>
          <p className="text-white/70 whitespace-pre-wrap">{r.response.text}</p>
          <div className="flex flex-wrap gap-3 mt-1">{(r.response.files || []).map((f) => (
            <a key={f.id} href={`${API}/files/${f.id}/download?token=${dl}`} target="_blank" rel="noreferrer" className="text-xs text-[#D4AF37] inline-flex items-center gap-1 hover:underline"><Paperclip size={12} /> {f.original_filename}</a>))}</div>
        </div>
      ) : open && <p className="text-white/40 text-xs mt-2">Zatiaľ bez vyjadrenia.{!r.email && " Dotyčný nemá e-mail ani účet – kontaktujte ho sami."}</p>}
      {r.decision_note && <p className="text-white/50 text-xs mt-2">Odôvodnenie rozhodnutia: {r.decision_note}</p>}
      <div className="flex flex-wrap gap-2 mt-3">
        {open && <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Odôvodnenie rozhodnutia (pôjde v e-maile)" className={`${inp} flex-1 min-w-[200px] text-xs`} data-testid={`rev-note-${r.id}`} />}
        {open && <button className={`${b} border-amber-400/60 text-amber-300`} onClick={() => act("warn", "Udeliť písomné napomenutie? Certifikát ostane platný.")} data-testid={`rev-warn-${r.id}`}>Napomenúť</button>}
        {open && <button className={`${b} bg-red-600 border-red-600 text-white`} onClick={() => act("revoke", "Odobrať certifikát? Zmizne zo Siene slávy a overenie ukáže „nenájdené“.")} data-testid={`rev-revoke-${r.id}`}>Odobrať certifikát</button>}
        {open && <button className={`${b} border-white/30`} onClick={() => act("close", "Zastaviť konanie? Certifikát ostane platný.")} data-testid={`rev-close-${r.id}`}>Zastaviť konanie</button>}
        {r.status === "revoked" && <button className={`${b} border-white/30`} onClick={() => act("reinstate", "Vrátiť certifikát? Znovu sa zobrazí v Sieni slávy.")} data-testid={`rev-reinstate-${r.id}`}>Vrátiť certifikát</button>}
      </div>
    </div>
  );
}

export default function AdminRevocations({ token }) {
  const hdr = { headers: { Authorization: `Bearer ${token}` } };
  const [q, setQ] = useState("");
  const [hits, setHits] = useState(null);
  const [rows, setRows] = useState([]);
  const load = useCallback(() => axios.get(`${API}/admin/revocations`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setRows(r.data.rows)).catch(() => {}), [token]);
  useEffect(() => { load(); }, [load]);
  const search = async (e) => {
    e.preventDefault();
    try { const { data } = await axios.get(`${API}/admin/revocations/search`, { ...hdr, params: { q } }); setHits(data.results); } catch { setHits([]); }
  };
  const refresh = () => { load(); if (q.trim().length >= 2) search({ preventDefault() {} }); };
  return (
    <div className="border border-red-400/30 bg-[#0a0a0a] p-5 mb-8" data-testid="admin-revocations">
      <p className="flex items-center gap-2 font-display text-lg"><Gavel size={17} className="text-red-300" /> Konanie o odobratí certifikátu</p>
      <p className="text-white/40 text-xs mt-1">Vyhľadajte certifikovaného profesionála alebo firmu. Výzva dá dotyčnému 30 dní na vyjadrenie v jeho portáli, potom rozhodnete.</p>
      <form onSubmit={search} className="mt-4 flex gap-2">
        <div className="relative flex-1"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Meno, firma, IČO, e-mail alebo číslo certifikátu…" className={`${inp} pl-9`} data-testid="rev-search-input" /></div>
        <button className="bg-[#D4AF37] text-black text-sm font-semibold px-5" data-testid="rev-search-btn">Hľadať</button>
      </form>
      {hits && <div className="mt-3 space-y-2">{hits.length ? hits.map((s) => <Hit key={s.certificate_no} s={s} hdr={hdr} onDone={refresh} />)
        : <p className="text-white/40 text-sm" data-testid="rev-search-none">Žiadny platný certifikát pre „{q}“.</p>}</div>}
      <p className="text-white/50 text-xs uppercase tracking-wider mt-6 mb-2">Konania ({rows.length})</p>
      <div className="space-y-3">{rows.length ? rows.map((r) => <Case key={r.id} r={r} hdr={hdr} token={token} onDone={refresh} />) : <p className="text-white/40 text-sm">Zatiaľ žiadne konania.</p>}</div>
    </div>
  );
}
